import React, { useState } from 'react';
import { assignLocation, AssignLocationRequest, unassignLocation } from '../../services/locationData';
import { Location } from '../../types/Locations';
import { Sensor } from '../../types/Sensor';
import { Device } from '../../types/Device';
import { BigDisplay } from '../../types/BigDisplay';
import { binIcon } from '../../utils/images';

import './AssignLocationForm.scss';

interface AssignLocationFormProps {
  isOpen: boolean;
  onClose: () => void;
  locations: Location[];
  item: Sensor | Device | BigDisplay | Device;
  itemType: 'sensor' | 'screen' | 'display';
  onSuccess: () => void;
}

export const AssignLocationForm: React.FC<AssignLocationFormProps> = ({
  isOpen,
  onClose,
  locations,
  item,
  itemType,
  onSuccess
}) => {
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const getItemId = () => {
    if (itemType === 'sensor') {
      return (item as Sensor).id;
    } else if (itemType === 'screen') {
      return (item as Device).id;
    } else {
      return (item as BigDisplay).id.toString();
    }
  };

  const getItemMac = () => {
    if (itemType === 'sensor') {
      return (item as Sensor).mac_address;
    } else if (itemType === 'screen') {
      return (item as Device).mac;
    } else {
      return (item as BigDisplay).android_id || 'N/A';
    }
  };

  const getItemOrientation = () => {
    if (itemType === 'display') {
      return (item as BigDisplay).orientation || 'N/A';
    } else {
      return 'N/A';
    }
  }

  const getItemType = () => {
    if (itemType === 'sensor') {
      return (item as Sensor).sensor_type;
    } else if (itemType === 'screen') {
      return `${(item as Device).screenSize}"`;
    } else {
      return 'Big Display';
    }
  };

  const getCurrentLocation = () => {
    if (itemType === 'sensor') {
      return (item as Sensor).location_code || 'Not assigned';
    } else if (itemType === 'screen') {
      return (item as Device).location_code || 'Not assigned';
    } else if (itemType === 'display') {
      return (item as BigDisplay).location_code || 'Not assigned';
    } else {
      return 'Not assigned'; // Big displays don't have location_code in the type
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocationId) return;

    setIsSubmitting(true);
    try {
      const selectedLocation = locations.find(loc => loc.id === selectedLocationId);
      if (!selectedLocation) throw new Error('Selected location not found');

      const storeId = localStorage.getItem('storeId');
      if (!storeId) throw new Error('Store ID not found');

      const assignData: AssignLocationRequest = {
        location_id: selectedLocation.id,
        location_code: selectedLocation.location_code,
        store_id: storeId,
      };

      if (itemType === 'sensor') {
        assignData.sensor_id = parseInt((item as Sensor).id);
      } else if (itemType === 'screen') {
        assignData.label_id = (item as Device).id;
      } else {
        assignData.display_id = (item as BigDisplay).id;
      }

      console.log('assignData being sent:', assignData);
      console.log('device.id as string:', (item as Device).id);

      if (item.location_code) {
        await unassignLocation(assignData);
      }

      await assignLocation(assignData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning location:', error);
      alert('Failed to assign location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    setIsSubmitting(true);
    try {
      const storeId = localStorage.getItem('storeId');
      if (!storeId) throw new Error('Store ID not found');

      const unassignData: AssignLocationRequest = {
        location_id: 0, // Not needed for unassign
        location_code: '', // Not needed for unassign
        store_id: storeId,
      };

      if (itemType === 'sensor') {
        unassignData.sensor_id = parseInt((item as Sensor).id);
      } else if (itemType === 'screen') {
        unassignData.label_id = (item as Device).id;
      } else {
        unassignData.display_id = (item as BigDisplay).id;
      }

      await unassignLocation(unassignData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error unassigning location:', error);
      alert('Failed to unassign location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCurrentLocation = () => {
    if (itemType === 'sensor') {
      return !!(item as Sensor).location_code;
    } else if (itemType === 'screen') {
      return !!(item as Device).location_code;
    } else if (itemType === 'display') {
      return !!(item as BigDisplay).location_code;
    }
    return false;
  };

  console.log("Selected display:", item, itemType);

  return (
    <div className="assign-form-overlay">
      <form className="assign-form" onSubmit={handleSubmit}>
        <div className="assign-form__header">
          <h3>Assign Location</h3>
          <div className="assign-form__actions">
            <button type="submit" disabled={!selectedLocationId || isSubmitting}>
              {isSubmitting ? 'Assigning...' : 'Assign'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
        <div className="assign-form__fields">
          <div className="assign-form__field">
            <label>Current Location</label>
            <div className="assign-form__field-with-icon">
              <input
                type="text"
                value={getCurrentLocation()}
                className="assign-form__field"
              />
              {hasCurrentLocation() && (
                <img
                  src={binIcon}
                  alt="Remove location"
                  className="assign-form__delete-icon"
                  onClick={handleUnassign}
                />
              )}
            </div>
          </div>
          <div className="assign-form__field">
            <label>
              <span className="assign-form__field--required">*</span>New Location
            </label>
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value ? parseInt(e.target.value) : '')}
              required
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.location_code}
                </option>
              ))}
            </select>
          </div>
          <div className="assign-form__field">
            <label>Type / Screen Size</label>
            <input
              type="text"
              value={getItemType()}
              disabled
              className="assign-form__field--disabled"
            />
          </div>
          <div className="assign-form__field">
            <label>Mac Address</label>
            <input
              type="text"
              value={getItemMac()}
              disabled
              className="assign-form__field--disabled"
            />
          </div>
          <div className="assign-form__field">
            <label>Id</label>
            <input
              type="text"
              value={getItemId()}
              disabled
              className="assign-form__field--disabled"
            />
          </div>
          {itemType === 'display' && (
            <div className="assign-form__field">
              <label>Orientation</label>
              <input
                type="text"
                value={getItemOrientation()}
                disabled
                className="assign-form__field--disabled"
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
}; 